import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type CSSProperties,
    type ReactElement,
} from "react";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { Badge, Button, Text, Stack } from "@ux/primitives";
import { palette, radius, space, typography } from "@ux/tokens";
import type { FloorplanHoverInfo, FloorplanMarker } from "../../admin/workspaces/types";
import {
    applyViewBox,
    centerOnElement,
    currentZoom,
    ensureContentBox,
    fitView,
    zoomAtPoint,
    type ViewBox,
} from "./engine/viewBox";
import {
    buildDeskHitZones,
    installGroupNameLabels,
    isHoverableSpaceId,
    markInteractiveElements,
    pickDeskUnderClientPoint,
    refreshOccupancyClasses,
    type DeskHitZone,
} from "./engine/svgSetup";
import "./floorplan.css";

const TAP_MOVE_MAX_PX = 6;

export interface FloorplanViewportProps {
    readonly svgUrl: string;
    readonly markers: readonly FloorplanMarker[];
    readonly hoverInfoByElementId: ReadonlyMap<string, FloorplanHoverInfo>;
    readonly selectedId: string | null;
    readonly searchMatchIds: ReadonlySet<string> | null;
    readonly onSelect: (elementId: string | null) => void;
    readonly focusRequest: { readonly elementId: string; readonly token: number } | null;
}

export function FloorplanViewport({
    svgUrl,
    markers,
    hoverInfoByElementId,
    selectedId,
    searchMatchIds,
    onSelect,
    focusRequest,
}: FloorplanViewportProps): ReactElement {
    const stageRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLDivElement>(null);
    const viewRef = useRef<ViewBox>({ vx: 0, vy: 0, vw: 1, vh: 1 });
    const contentRef = useRef<ViewBox>({ vx: 0, vy: 0, vw: 1, vh: 1 });
    const zonesRef = useRef<DeskHitZone[]>([]);
    const markersRef = useRef(markers);
    const hoverInfoRef = useRef(hoverInfoByElementId);
    const hoverIdRef = useRef<string | null>(null);
    const panRef = useRef<{
        pointerId: number;
        px: number;
        py: number;
        vsx: number;
        vsy: number;
        isPanning: boolean;
    } | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [zoomLabel, setZoomLabel] = useState("1.00");
    const [tooltip, setTooltip] = useState<{
        elementId: string;
        x: number;
        y: number;
    } | null>(null);

    markersRef.current = markers;
    hoverInfoRef.current = hoverInfoByElementId;

    const getSvg = useCallback((): SVGSVGElement | null => {
        return canvasRef.current?.querySelector("svg") ?? null;
    }, []);

    const syncZoomLabel = useCallback(() => {
        setZoomLabel(currentZoom(contentRef.current, viewRef.current).toFixed(2));
    }, []);

    const apply = useCallback(() => {
        const svg = getSvg();
        if (!svg) return;
        applyViewBox(svg, viewRef.current);
        syncZoomLabel();
    }, [getSvg, syncZoomLabel]);

    const deskPointerTargetMatches = useCallback((t: Element | null): boolean => {
        if (!t?.id) return false;
        const roster = markersRef.current;
        if (roster.some((m) => m.elementId === t.id)) return true;
        if (t.classList.contains("fp-zone") || t.classList.contains("fp-assigned")) return true;
        if (t.tagName.toLowerCase() === "g" && isHoverableSpaceId(t.id)) return true;
        return false;
    }, []);

    const rebuildZones = useCallback(() => {
        const svg = getSvg();
        if (!svg) {
            zonesRef.current = [];
            return;
        }
        zonesRef.current = buildDeskHitZones(svg, deskPointerTargetMatches);
    }, [deskPointerTargetMatches, getSvg]);

    const clearHover = useCallback(() => {
        const svg = getSvg();
        hoverIdRef.current = null;
        setTooltip(null);
        if (!svg) return;
        for (const el of svg.querySelectorAll(".fp-hover")) {
            el.classList.remove("fp-hover");
        }
    }, [getSvg]);

    const setHover = useCallback(
        (el: Element | null, clientX?: number, clientY?: number) => {
            if (!el?.id) {
                clearHover();
                return;
            }
            if (el.id !== hoverIdRef.current) {
                const svg = getSvg();
                if (svg) {
                    for (const node of svg.querySelectorAll(".fp-hover")) {
                        node.classList.remove("fp-hover");
                    }
                    el.classList.add("fp-hover");
                }
                hoverIdRef.current = el.id;
            }
            if (
                typeof clientX === "number" &&
                typeof clientY === "number" &&
                Number.isFinite(clientX) &&
                Number.isFinite(clientY)
            ) {
                setTooltip({ elementId: el.id, x: clientX, y: clientY });
            }
        },
        [clearHover, getSvg],
    );

    const resolveDesk = useCallback(
        (clientX: number, clientY: number): Element | null => {
            return pickDeskUnderClientPoint(
                zonesRef.current,
                clientX,
                clientY,
                deskPointerTargetMatches,
            );
        },
        [deskPointerTargetMatches],
    );

    // Load SVG whenever the office asset URL changes.
    useEffect(() => {
        let cancelled = false;
        const canvas = canvasRef.current;
        if (!canvas) return;

        setLoading(true);
        setError(null);
        canvas.innerHTML = "";

        void (async () => {
            try {
                const res = await fetch(svgUrl);
                if (!res.ok) throw new Error(`SVG fetch failed: ${res.status}`);
                const text = await res.text();
                if (cancelled) return;
                canvas.innerHTML = text;
                const svg = canvas.querySelector("svg");
                if (!svg) throw new Error("Floorplan SVG root missing");
                svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
                contentRef.current = ensureContentBox(svg);
                viewRef.current = fitView(contentRef.current);
                markInteractiveElements(svg, markersRef.current);
                rebuildZones();
                applyViewBox(svg, viewRef.current);
                syncZoomLabel();
                setLoading(false);
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : "Failed to load floorplan");
                setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [svgUrl, rebuildZones, syncZoomLabel]);

    // Keep occupancy paints + desk name labels in sync when assignments change.
    useEffect(() => {
        const svg = getSvg();
        if (!svg || loading) return;
        refreshOccupancyClasses(svg, markers);
        installGroupNameLabels(svg, markers);
        rebuildZones();
    }, [markers, getSvg, loading, rebuildZones]);

    // Selection + search dimming.
    useEffect(() => {
        const svg = getSvg();
        if (!svg || loading) return;

        for (const el of svg.querySelectorAll(".fp-selected, .fp-dimmed, .fp-search-dim")) {
            el.classList.remove("fp-selected", "fp-dimmed", "fp-search-dim");
        }

        if (selectedId) {
            const el = svg.getElementById(selectedId);
            if (el) {
                el.classList.add("fp-selected");
                for (const other of svg.querySelectorAll(".fp-assigned, .fp-zone")) {
                    if (other !== el) other.classList.add("fp-dimmed");
                }
            }
            return;
        }

        if (searchMatchIds && searchMatchIds.size > 0) {
            for (const other of svg.querySelectorAll(".fp-assigned, .fp-zone")) {
                if (!searchMatchIds.has(other.id)) {
                    other.classList.add("fp-search-dim");
                }
            }
        }
    }, [selectedId, searchMatchIds, getSvg, loading]);

    // Auto-pan when search / programmatic focus requests a desk.
    useEffect(() => {
        if (!focusRequest || loading) return;
        const svg = getSvg();
        const stage = stageRef.current;
        if (!svg || !stage) return;
        const el = svg.getElementById(focusRequest.elementId);
        if (!el) return;
        viewRef.current = centerOnElement(
            contentRef.current,
            svg,
            el as SVGGraphicsElement,
            stage.clientWidth,
            stage.clientHeight,
        );
        apply();
    }, [focusRequest, loading, getSvg, apply]);

    // Pointer pan / tap + wheel zoom.
    useEffect(() => {
        const stage = stageRef.current;
        const canvas = canvasRef.current;
        if (!stage || !canvas) return;

        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const svg = getSvg();
            if (!svg) return;
            const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
            viewRef.current = zoomAtPoint(
                contentRef.current,
                viewRef.current,
                svg,
                e.clientX,
                e.clientY,
                factor,
            );
            apply();
        };

        const onPointerDown = (e: PointerEvent) => {
            if (e.button !== 0) return;
            panRef.current = {
                pointerId: e.pointerId,
                px: e.clientX,
                py: e.clientY,
                vsx: viewRef.current.vx,
                vsy: viewRef.current.vy,
                isPanning: false,
            };
            canvas.classList.add("fp-grabbing");
            canvas.setPointerCapture(e.pointerId);
        };

        const onPointerMove = (e: PointerEvent) => {
            const pan = panRef.current;
            if (!pan || pan.pointerId !== e.pointerId) {
                if (pointerIn(stage, e.clientX, e.clientY)) {
                    setHover(resolveDesk(e.clientX, e.clientY), e.clientX, e.clientY);
                }
                return;
            }
            const dx = e.clientX - pan.px;
            const dy = e.clientY - pan.py;
            if (!pan.isPanning) {
                if (Math.hypot(dx, dy) < TAP_MOVE_MAX_PX) {
                    setHover(resolveDesk(e.clientX, e.clientY), e.clientX, e.clientY);
                    return;
                }
                pan.isPanning = true;
                clearHover();
            }
            const svg = getSvg();
            if (!svg) return;
            const r = svg.getBoundingClientRect();
            if (!r.width || !r.height) return;
            viewRef.current = {
                ...viewRef.current,
                vx: pan.vsx - dx * (viewRef.current.vw / r.width),
                vy: pan.vsy - dy * (viewRef.current.vh / r.height),
            };
            apply();
        };

        const endPan = (e: PointerEvent) => {
            const pan = panRef.current;
            if (!pan || pan.pointerId !== e.pointerId) return;
            const wasTap = !pan.isPanning;
            panRef.current = null;
            canvas.classList.remove("fp-grabbing");
            try {
                canvas.releasePointerCapture(e.pointerId);
            } catch {
                /* already released */
            }
            if (wasTap && e.type === "pointerup" && e.button === 0) {
                const hit = resolveDesk(e.clientX, e.clientY);
                onSelect(hit?.id ?? null);
            }
        };

        const onPointerLeave = () => {
            clearHover();
        };

        stage.addEventListener("wheel", onWheel, { passive: false });
        canvas.addEventListener("pointerdown", onPointerDown);
        canvas.addEventListener("pointermove", onPointerMove);
        canvas.addEventListener("pointerup", endPan);
        canvas.addEventListener("pointercancel", endPan);
        stage.addEventListener("pointerleave", onPointerLeave);

        return () => {
            stage.removeEventListener("wheel", onWheel);
            canvas.removeEventListener("pointerdown", onPointerDown);
            canvas.removeEventListener("pointermove", onPointerMove);
            canvas.removeEventListener("pointerup", endPan);
            canvas.removeEventListener("pointercancel", endPan);
            stage.removeEventListener("pointerleave", onPointerLeave);
        };
    }, [apply, clearHover, getSvg, onSelect, resolveDesk, setHover]);

    function zoomBy(factor: number): void {
        const svg = getSvg();
        const stage = stageRef.current;
        if (!svg || !stage) return;
        const r = stage.getBoundingClientRect();
        viewRef.current = zoomAtPoint(
            contentRef.current,
            viewRef.current,
            svg,
            r.left + r.width / 2,
            r.top + r.height / 2,
            factor,
        );
        apply();
    }

    function resetView(): void {
        viewRef.current = fitView(contentRef.current);
        apply();
        onSelect(null);
    }

    const occupiedCount = markers.filter((m) => m.kind === "desk" && m.occupied).length;
    const vacantCount = markers.filter((m) => m.kind === "desk" && !m.occupied).length;

    return (
        <div ref={stageRef} className="fp-root" style={stageStyle}>
            <div ref={canvasRef} className="fp-canvas" />

            {(loading || error) && (
                <div style={overlayStyle}>
                    {error ? (
                        <Text role="muted" color={palette.danger}>
                            {error}
                        </Text>
                    ) : (
                        <Text role="muted">Loading floorplan…</Text>
                    )}
                </div>
            )}

            <div style={statusStyle}>
                <Stack direction="row" gap={2} align="center" wrap>
                    <Badge tone="accent">zoom {zoomLabel}×</Badge>
                    <Badge tone="accent">{occupiedCount} occupied</Badge>
                    <Badge tone="ok">{vacantCount} vacant</Badge>
                </Stack>
            </div>

            <div style={zoomToolbarStyle}>
                <Button
                    variant="secondary"
                    size="sm"
                    aria-label="Zoom in"
                    onClick={() => zoomBy(1.25)}
                    leadingIcon={<Plus size={16} />}
                >
                    In
                </Button>
                <Button
                    variant="secondary"
                    size="sm"
                    aria-label="Zoom out"
                    onClick={() => zoomBy(1 / 1.25)}
                    leadingIcon={<Minus size={16} />}
                >
                    Out
                </Button>
                <Button
                    variant="secondary"
                    size="sm"
                    aria-label="Reset view"
                    onClick={resetView}
                    leadingIcon={<RotateCcw size={16} />}
                >
                    Reset
                </Button>
            </div>

            {tooltip ? (
                <HoverTooltip
                    info={
                        hoverInfoByElementId.get(tooltip.elementId) ??
                        unmappedHoverInfo(tooltip.elementId)
                    }
                    x={tooltip.x}
                    y={tooltip.y}
                />
            ) : null}
        </div>
    );
}

function unmappedHoverInfo(elementId: string): FloorplanHoverInfo {
    return {
        title: elementId,
        statusLabel: "Not linked",
        rows: [
            { label: "Map id", value: elementId },
            {
                label: "Status",
                value: "No workspace mapped — click to create or link one",
            },
        ],
    };
}

function HoverTooltip({
    info,
    x,
    y,
}: {
    readonly info: FloorplanHoverInfo;
    readonly x: number;
    readonly y: number;
}): ReactElement {
    const pad = 14;
    const left = Math.min(x + pad, window.innerWidth - 300);
    const top = Math.min(y + pad, window.innerHeight - 200);
    return (
        <div
            style={{
                ...tooltipStyle,
                left,
                top,
            }}
        >
            <div style={tooltipTitleStyle}>{info.title}</div>
            <div style={tooltipStatusStyle}>{info.statusLabel}</div>
            {info.rows.map((row) => (
                <div key={`${row.label}:${row.value}`} style={tooltipRowStyle}>
                    <span style={tooltipLabelStyle}>{row.label}</span>
                    <span>{row.value}</span>
                </div>
            ))}
        </div>
    );
}

function pointerIn(el: HTMLElement, x: number, y: number): boolean {
    const r = el.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
}

const stageStyle: CSSProperties = {
    position: "relative",
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: "hidden",
    background: `
        linear-gradient(45deg, rgba(255,255,255,0.03) 25%, transparent 25%),
        linear-gradient(-45deg, rgba(255,255,255,0.03) 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, rgba(255,255,255,0.03) 75%),
        linear-gradient(-45deg, transparent 75%, rgba(255,255,255,0.03) 75%)
    `,
    backgroundSize: "20px 20px",
    backgroundPosition: "0 0, 0 10px, 10px -10px, 10px 0",
    backgroundColor: palette.canvas,
};

const overlayStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    display: "grid",
    placeItems: "center",
    background: palette.canvas,
    zIndex: 20,
};

const statusStyle: CSSProperties = {
    position: "absolute",
    left: space[3],
    bottom: space[3],
    zIndex: 5,
    padding: `${space[2]} ${space[3]}`,
    borderRadius: radius.md,
    border: `1px solid ${palette.border}`,
    background: palette.surfaceRaised,
    fontFamily: typography.family.body,
};

const zoomToolbarStyle: CSSProperties = {
    position: "absolute",
    right: space[3],
    bottom: space[3],
    zIndex: 5,
    display: "flex",
    flexDirection: "column",
    gap: space[1],
};

const tooltipStyle: CSSProperties = {
    position: "fixed",
    zIndex: 100,
    pointerEvents: "none",
    minWidth: 220,
    maxWidth: 320,
    padding: `${space[3]} ${space[3]}`,
    borderRadius: radius.md,
    border: `1px solid ${palette.borderStrong}`,
    background: "#15171c",
    boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
    fontFamily: typography.family.body,
    color: palette.fg,
};

const tooltipTitleStyle: CSSProperties = {
    fontWeight: typography.weight.semibold,
    fontSize: typography.size.md,
    color: palette.accentFg,
    marginBottom: 4,
};

const tooltipStatusStyle: CSSProperties = {
    fontSize: typography.size.xs,
    color: palette.fgMuted,
    marginBottom: space[2],
    textTransform: "uppercase",
    letterSpacing: "0.04em",
};

const tooltipRowStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    fontSize: typography.size.sm,
    marginTop: space[1],
    color: palette.fg,
};

const tooltipLabelStyle: CSSProperties = {
    fontSize: typography.size.xs,
    color: palette.fgSubtle,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
};
